import { ProjectForm } from '../components/project/ProjectForm';
import { ApplicationList } from '../components/applications/ApplicationList';
import VMTable from '../components/vms/VMTable';
import { SizingConfigPanel } from '../components/sizing-config/SizingConfigPanel';
import { ResultsPage } from './ResultsPage';
import { useUiStore } from '../store/uiStore';
import { useAutoSave } from '../hooks/useAutoSave';

const s = {
  stepHeading: {
    marginBottom: '28px',
  },
  h1: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#E8EDF2',
    marginBottom: '4px',
    fontFamily: "'Poppins', sans-serif",
  },
  sub: {
    fontSize: '13px',
    color: '#8FA4B5',
  },
  placeholder: {
    border: '1px dashed rgba(76,92,104,0.35)',
    borderRadius: '8px',
    padding: '48px 24px',
    textAlign: 'center' as const,
    color: '#4C5C68',
  },
  placeholderTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#8FA4B5',
    marginBottom: '4px',
  },
  placeholderSub: {
    fontSize: '13px',
    color: '#4C5C68',
    marginBottom: '20px',
  },
  backBtn: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#8FA4B5',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
  },
};

const stepMeta: Record<number, { title: string; subtitle: string }> = {
  1: { title: 'Project Details', subtitle: 'Name this engagement and identify the client.' },
  2: { title: 'Application Registry', subtitle: 'Define the application groups that make up this environment.' },
  3: { title: 'Virtual Machines', subtitle: 'Configure the VMs within each application group.' },
  4: { title: 'Sizing Configuration', subtitle: 'Set overcommit ratios, HA model, DR parameters, and storage architecture.' },
  5: { title: 'Results & BOM', subtitle: 'Review the computed infrastructure BOM.' },
};

export function ProjectEditor() {
  useAutoSave();
  const currentStep = useUiStore(st => st.currentStep);
  const setCurrentStep = useUiStore(st => st.setCurrentStep);

  const { title, subtitle } = stepMeta[currentStep] ?? stepMeta[1];

  return (
    <>
      <div style={s.stepHeading}>
        <h1 style={s.h1}>Step {currentStep}: {title}</h1>
        <p style={s.sub}>{subtitle}</p>
      </div>

      {currentStep === 1 && (
        <ProjectForm onNext={() => setCurrentStep(2)} />
      )}

      {currentStep === 2 && (
        <ApplicationList
          onNext={() => setCurrentStep(3)}
          onBack={() => setCurrentStep(1)}
        />
      )}

      {currentStep === 3 && (
        <>
          <VMTable />
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button type="button" style={s.backBtn} onClick={() => setCurrentStep(2)}>
              ← Back to Applications
            </button>
            <button type="button" style={s.backBtn} onClick={() => setCurrentStep(4)}>
              Next: Sizing Configuration →
            </button>
          </div>
        </>
      )}

      {currentStep === 4 && (
        <>
          <SizingConfigPanel />
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button type="button" style={s.backBtn} onClick={() => setCurrentStep(3)}>
              ← Back to Virtual Machines
            </button>
            <button type="button" style={s.backBtn} onClick={() => setCurrentStep(5)}>
              Next: Results & BOM →
            </button>
          </div>
        </>
      )}

      {currentStep === 5 && (
        <>
          <ResultsPage />
          <div style={{ marginTop: 24 }}>
            <button type="button" style={s.backBtn} onClick={() => setCurrentStep(4)}>
              ← Back to Sizing Configuration
            </button>
          </div>
        </>
      )}
    </>
  );
}
